import React from 'react';
import { Project, ProjectPlanStep, ProjectKnowledgeBase, ProjectFile } from '../../../types';
import { ProjectIcon } from '../../../components/layout/Sidebar';
import { TelemetryLog } from '../hooks/useProjectStream';

interface LivingDashboardProps {
  project: Project;
  liveSteps: ProjectPlanStep[];
  telemetry: Record<string, TelemetryLog>;
  liveKnowledge: ProjectKnowledgeBase[];
  liveFiles: ProjectFile[];
  onEditObjective: () => void;
}

export const LivingDashboard: React.FC<LivingDashboardProps> = ({ 
  project, 
  liveSteps, 
  telemetry, 
  liveKnowledge, 
  liveFiles,
  onEditObjective
}) => {
  const briefText = project.situation || "The project pipeline is currently active and processing background tasks.";

  return (
    <div className="flex-1 overflow-y-auto no-scrollbar bg-white dark:bg-[#050505] text-gray-900 dark:text-gray-100 p-6 md:p-10 transition-colors duration-300 font-sans">
      <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        
        {/* Top Banner: Dynamic Status Brief */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#121212] p-8 md:p-10 shadow-sm relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-1 h-full bg-black dark:bg-white" />
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button onClick={onEditObjective} className="text-gray-400 hover:text-black dark:hover:text-white transition-colors" title="Edit Objective">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
              </button>
              <div className="flex items-center gap-3">
                <ProjectIcon name={project.icon} className="w-5 h-5 text-gray-900 dark:text-white" />
                <span className="text-sm font-bold text-gray-900 dark:text-white font-sans">{project.name}</span>
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400 text-[10px] font-bold uppercase tracking-wider border border-green-200 dark:border-green-900">
                <div className="w-1.5 h-1.5 rounded-full bg-green-600 dark:bg-green-500 animate-pulse" />
                EXECUTING
              </span>
            </div>
            <button className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center gap-2">
               <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
               View Plan Details
            </button>
          </div>
          <div className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white leading-relaxed font-sans max-w-5xl">
            "{briefText}"
          </div>
          
          <div className="mt-8 flex items-center gap-4">
             <div className="flex -space-x-2">
               <div className="w-8 h-8 rounded-full border-2 border-white dark:border-[#121212] bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-[10px] font-bold">N</div>
               <div className="w-8 h-8 rounded-full border-2 border-white dark:border-[#121212] bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-[10px] font-bold text-blue-700 dark:text-blue-300"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
             </div>
             <span className="text-xs font-bold text-gray-500 dark:text-gray-400">Neura is actively managing {liveSteps.filter(s => s.status !== 'completed').length} operations</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          
          {/* Left Panel: The Blueprint Timeline */}
          <div className="lg:col-span-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#121212] p-8 shadow-sm flex flex-col">
            <div className="flex items-center justify-between mb-10">
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-900 dark:text-white"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M7 7h10"/><path d="M7 12h10"/><path d="M7 17h10"/></svg>
                <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest font-sans">The Blueprint Timeline</h3>
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Live Execution</span>
            </div>

            <div className="relative pl-6 border-l border-gray-200 dark:border-gray-800 space-y-10 flex-1">
              {liveSteps.map((step, idx) => {
                const isDone = step.status === 'completed';
                const isRunning = step.status === 'running';
                const log = telemetry[step.id];
                const isSleeping = log?.status === 'dormant';
                
                return (
                  <div key={step.id} className={`relative transition-opacity duration-300 ${isSleeping ? 'opacity-40' : 'opacity-100'}`}>
                    <div className={`absolute -left-[37px] top-1 w-8 h-8 rounded-full flex items-center justify-center border-2 bg-white dark:bg-[#121212] shadow-sm ${
                      isDone ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white' :
                      isRunning ? 'border-blue-500 text-blue-500 bg-blue-50 dark:bg-blue-950/20' :
                      'border-gray-300 dark:border-gray-700 text-gray-400 dark:text-gray-600'
                    }`}>
                      {isDone && <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>}
                      {isRunning && <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="animate-spin"><path d="M12 2v4"/><path d="M12 18v4"/><path d="M4.93 4.93l2.83 2.83"/><path d="M16.24 16.24l2.83 2.83"/><path d="M2 12h4"/><path d="M18 12h4"/><path d="M4.93 19.07l2.83-2.83"/><path d="M16.24 7.76l2.83-2.83"/></svg>}
                      {isSleeping && <span className="text-[12px]">💤</span>}
                      {(!isDone && !isRunning && !isSleeping) && <span className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-700" />}
                    </div>

                    <div className="pt-2">
                      <div className="flex items-center gap-3">
                         <span className={`text-[10px] font-bold uppercase tracking-widest ${isDone ? 'text-gray-400' : isRunning ? 'text-blue-500' : 'text-gray-500'}`}>Phase {idx + 1}</span>
                         {isSleeping && <span className="text-[9px] font-bold uppercase tracking-widest bg-gray-100 dark:bg-gray-800 text-gray-500 px-2 py-0.5 rounded-md">Dormant</span>}
                      </div>
                      <h4 className={`text-base font-bold font-sans mt-1 ${isDone ? 'text-gray-900 dark:text-white line-through opacity-70' : isRunning ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                        {step.task}
                      </h4>
                      
                      {isRunning && log?.status === 'processing' && (
                        <div className="mt-4 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-100 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-2 animate-in fade-in slide-in-from-top-2 duration-300 relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-1 h-full bg-blue-500" />
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Active Tool Execution</span>
                          </div>
                          <span className="text-sm font-bold text-gray-800 dark:text-gray-300 font-mono mt-1">{log.toolName || 'Tool Running...'}</span>
                          <span className="text-xs text-gray-500 font-mono">{log.message}</span>
                        </div>
                      )}

                      {isSleeping && (
                        <div className="mt-3 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-white/5 inline-flex flex-col gap-1.5 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-800/50">
                          <span className="flex items-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> Condition: {log.waitCondition}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel: Knowledge & Deliverables */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            
            {/* Extracted Knowledge Base */}
            <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#121212] p-8 shadow-sm flex flex-col min-h-[250px]">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-900 dark:text-white"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>
                  <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest font-sans">Extracted Knowledge</h3>
                </div>
                <span className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800 text-[10px] font-bold flex items-center justify-center text-gray-600 dark:text-gray-400">{liveKnowledge.length}</span>
              </div>
              
              <div className="space-y-4 text-sm font-sans flex-1 overflow-y-auto no-scrollbar">
                {liveKnowledge.length === 0 && <span className="text-gray-400 text-xs italic">No knowledge extracted yet.</span>}
                {liveKnowledge.map(k => (
                  <div key={k.id} className="group relative bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-gray-800 rounded-xl p-4 hover:border-gray-300 dark:hover:border-gray-700 transition-all cursor-pointer hover:shadow-sm">
                    <span className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">{k.category} / {k.key}</span>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white leading-relaxed">{k.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Generated Deliverables */}
            <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#121212] p-8 shadow-sm flex flex-col min-h-[250px]">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-900 dark:text-white"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest font-sans">Generated Deliverables</h3>
                </div>
              </div>
              
              <div className="flex flex-col gap-3">
                {liveFiles.length === 0 && <span className="text-gray-400 text-xs italic">No files generated yet.</span>}
                {liveFiles.map(f => (
                  <button key={f.id} className="flex items-center justify-between bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-gray-800 rounded-xl p-3.5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors text-left group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-500 flex items-center justify-center font-bold text-[11px] shadow-sm uppercase">{f.file_type}</div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white truncate max-w-[200px]">{f.name}</span>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};
